import { ArrowDown, ArrowUp, TriangleAlert, X } from 'lucide-react';
import { Button } from '../ui/button';
import { moveRow, removeRow, rowsNeedingMobile, setRowOn } from '../../lib/speedToLead/staffView';
import type { StaffRow } from '../../types/speedToLead';

// The ordered staff call list, shared by Kyck's settings page and the club's own portal page.
// PURE PRESENTATION over rows the page owns: order (up/down buttons, so it works on a phone and with a
// keyboard), an on/off switch per person, and a visible warning for anyone no longer on the club's team.
//
// ⚠️ Names and roles are READ from the club's team; this component never edits them. It is used in the
// portal, so it uses theme tokens only (no staff component library).

export function StaffListEditor({
  rows,
  onChange,
  disabled = false,
}: {
  rows: StaffRow[];
  onChange: (rows: StaffRow[]) => void;
  disabled?: boolean;
}) {
  const needMobile = rowsNeedingMobile(rows);
  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No team members yet. Add people to the club team first, then switch on who takes lead calls.
        </p>
      )}
      <ol className="divide-y divide-border rounded-md border border-border" aria-label="Staff call order">
        {rows.map((row, i) => (
          <li key={row.teamMemberId} className="flex flex-wrap items-center gap-3 p-3" data-removed={row.removed || undefined}>
            <span className="w-6 text-center text-sm tabular-nums text-muted-foreground" aria-hidden="true">
              {row.listed ? i + 1 : ''}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{row.name}</p>
              {row.role && <p className="truncate text-xs text-muted-foreground">{row.role}</p>}
              {row.removed && (
                <p role="alert" className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                  <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
                  No longer on the club team, so they will be skipped. Remove them from the list.
                </p>
              )}
              {!row.removed && row.receivesLeadCalls && !row.hasMobile && (
                <p role="alert" className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                  <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
                  No mobile number on the team profile, so they cannot be called.
                </p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={row.receivesLeadCalls}
                disabled={disabled || row.removed}
                onChange={(e) => onChange(setRowOn(rows, i, e.target.checked))}
                aria-label={`${row.name} takes lead calls`}
              />
              Takes lead calls
            </label>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={disabled || i === 0}
                aria-label={`Move ${row.name} up`}
                onClick={() => onChange(moveRow(rows, i, -1))}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={disabled || i === rows.length - 1}
                aria-label={`Move ${row.name} down`}
                onClick={() => onChange(moveRow(rows, i, 1))}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              {row.removed && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={disabled}
                  aria-label={`Remove ${row.name} from the list`}
                  onClick={() => onChange(removeRow(rows, i))}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </li>
        ))}
      </ol>
      {needMobile.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {needMobile.length} switched-on {needMobile.length === 1 ? 'person has' : 'people have'} no mobile number on the club team profile.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        People are called in this order. Switching someone off keeps their place in the list.
      </p>
    </div>
  );
}
